import type { LeagueScope, PermissionScopeResources } from "~types/permissionScope";
import { PermissionScopeTypes } from "../../enums/permissionScopeTypes";
import { CompetitionSwitcherIcon } from "../components";
import { NO_LEAGUE_ID } from "../constants/ui";
import type { PermissionScopeTreeNode } from "../types";

const transformScopeLeagueTree = (
  data: LeagueScope[],
  resources: PermissionScopeResources,
  allowedScopeLevel: PermissionScopeTypes,
): PermissionScopeTreeNode[] => {
  const { leagues: leaguesData = {}, competitions: competitionsData = {}, seasons: seasonsData = {} } = resources;

  const hasNoLeagues = data.length === 1 && !data[0].leagueId;
  const shouldShowSeasons = allowedScopeLevel === PermissionScopeTypes.Season;
  const shouldShowCompetitions = allowedScopeLevel !== PermissionScopeTypes.League;
  const isIndirectLeaguesAccess =
    allowedScopeLevel === PermissionScopeTypes.Competition || allowedScopeLevel === PermissionScopeTypes.Season;
  const isIndirectCompetitionsAccess = allowedScopeLevel === PermissionScopeTypes.Season;

  if (hasNoLeagues) {
    let competitions = data[0].competitions;
    if (shouldShowSeasons) {
      competitions = competitions.filter(({ seasonIds }) => seasonIds.length > 0);
    }
    return competitions.map(({ competitionId, seasonIds }) => ({
      value: competitionId,
      title: competitionsData[competitionId]?.nameLocal || "",
      key: competitionId,
      scopeType: PermissionScopeTypes.Competition,
      checkable: true,
      isIndirectAccess: isIndirectCompetitionsAccess,
      switcherIcon: shouldShowSeasons ? CompetitionSwitcherIcon : undefined,
      children: shouldShowSeasons
        ? seasonIds.map((seasonId) => ({
            value: seasonId,
            title: seasonsData[seasonId]?.nameLocal || "",
            key: seasonId,
            checkable: true,
            isIndirectAccess: false,
            scopeType: PermissionScopeTypes.Season,
          }))
        : [],
    }));
  }

  let filteredData = data;

  if (allowedScopeLevel === PermissionScopeTypes.League) {
    filteredData = data.filter((node) => node.leagueId);
  }
  if (allowedScopeLevel === PermissionScopeTypes.Competition) {
    filteredData = data.filter(({ competitions }) => competitions.length > 0);
  }
  if (allowedScopeLevel === PermissionScopeTypes.Season) {
    filteredData = data.filter(({ competitions }) => competitions.some(({ seasonIds }) => seasonIds.length > 0));
    filteredData.forEach((item) => {
      item.competitions = item.competitions.filter(({ seasonIds }) => seasonIds.length > 0);
    });
  }

  return filteredData.map(({ leagueId, competitions }) => ({
    value: leagueId || NO_LEAGUE_ID,
    title: leagueId ? leaguesData[leagueId]?.nameLocal || "" : "",
    key: leagueId || NO_LEAGUE_ID,
    scopeType: PermissionScopeTypes.League,
    checkable: !!leagueId,
    isIndirectAccess: isIndirectLeaguesAccess,
    children: shouldShowCompetitions
      ? competitions.map(({ competitionId, seasonIds }) => ({
          value: competitionId,
          title: competitionsData[competitionId]?.nameLocal || "",
          key: competitionId,
          scopeType: PermissionScopeTypes.Competition,
          checkable: true,
          switcherIcon: shouldShowSeasons ? CompetitionSwitcherIcon : undefined,
          isIndirectAccess: isIndirectCompetitionsAccess,
          children: shouldShowSeasons
            ? seasonIds.map((seasonId) => ({
                value: seasonId,
                title: seasonsData[seasonId]?.nameLocal || "",
                key: seasonId,
                scopeType: PermissionScopeTypes.Season,
                checkable: true,
                isIndirectAccess: false,
              }))
            : [],
        }))
      : [],
  }));
};

export default transformScopeLeagueTree;
